<script setup lang="ts">
import { storeToRefs } from 'pinia'
import { nextTick, ref, watch } from 'vue'

import { useConfigStore } from '../stores/config'
import { useKeepTabbingFocus } from '../composables/use-keep-tabbing-focus'
import HeaderSkipLink from './HeaderSkipLink.vue'
import Icon from './shared/Icon.vue'
import MenuList from './menu/MenuList.vue'
import SocialLinks from './shared/SocialLinks.vue'
import MenuGroupHeadline from './menu/MenuGroupHeadline.vue'

import type { Brand } from '@xcel-next/core/enums'
import type { ComposedMenu, ComposedPage } from '@xcel-next/composer-schema'

withDefaults(defineProps<{
  headerData?: NonNullable<ComposedPage['header']>,
  brand?: Brand
}>(), {
  headerData: () => ({})
})

const { rsConfig } = storeToRefs(useConfigStore())
const { keepTabbingFocus, removeTabbingEventListeners } = useKeepTabbingFocus()

const showBurgerMenu = ref(false)
const burgerButton = ref<HTMLButtonElement | null>(null)
const burger = ref<HTMLElement | null>(null)

const shouldAddExtendedMenuToBurger = (menu: ComposedMenu) =>
  menu.some((item) => item.includeInBurgerMenu)

const keepTabbingFocusInBurger = () => {
  nextTick(() => {
    removeTabbingEventListeners()
    if (!burger.value) return
    keepTabbingFocus(burger.value, ['a, button:not(.bx-header__burger-close-button)'], 0, () => {
      removeTabbingEventListeners()
      showBurgerMenu.value = false
      burgerButton.value?.focus()
    })
  })
}

watch(showBurgerMenu, (newValue) => {
  if (newValue) {
    document.body.classList.add('bx-js-dialog-opened')
    keepTabbingFocusInBurger()
  } else {
    document.body.classList.remove('bx-js-dialog-opened')
  }
})
</script>

<template>
  <HeaderSkipLink />
  <div class="bx-flooding bx-flooding--header">
    <header class="bx-header">
      <div class="bx-grid bx-header__wrapper">
        <slot name="header-logo" />
        <div class="bx-header__navi-wrapper">
          <MenuList :menu="headerData.menu"
                    menu-type="header"
                    menu-context="desktop"
                    :brand="brand" />
        </div>

        <slot name="header-search" />

        <button id="burger"
                ref="burgerButton"
                :class="{
                  'bx-header__burger': true,
                  'bx-header__burger--open': showBurgerMenu
                }"
                :aria-label="`Menü ${showBurgerMenu ? 'schließen' : 'öffnen'}`"
                :aria-expanded="showBurgerMenu"
                aria-haspopup="true"
                aria-controls="aria-control-burger-menu"
                @click="showBurgerMenu = !showBurgerMenu">
          <Icon v-if="showBurgerMenu"
                name="close" />
          <Icon v-else
                name="burger" />
        </button>

        <div v-if="showBurgerMenu"
             class="bx-header__burger-backdrop"
             @click="showBurgerMenu = false" />

        <div id="aria-control-burger-menu"
             ref="burger"
             :class="{
               'bx-header__burger-navi-wrapper': true,
               'bx-header__burger-navi-wrapper--open': showBurgerMenu
             }">
          <div class="bx-header__burger-navi-wrapper__close">
            <button class="bx-header__burger-close-button"
                    aria-label="Menü schließen"
                    aria-controls="aria-control-burger-menu"
                    :aria-expanded="showBurgerMenu"
                    @click="showBurgerMenu = false">
              <Icon name="close" />
            </button>
          </div>

          <MenuGroupHeadline v-if="$slots['header-channel-navigation']"
                             title="Andere Portale" />

          <slot name="header-channel-navigation" />

          <MenuGroupHeadline title="Kategorien" />

          <MenuList :menu="headerData.menu"
                    menu-type="header"
                    menu-context="burger"
                    :brand="brand" />

          <template v-for="(menu, index) in headerData.extendedMenus || []"
                    :key="`extended-menu-${index}`">
            <template v-if="shouldAddExtendedMenuToBurger(menu.menu ?? [])">
              <MenuGroupHeadline :title="menu.name" />
              <MenuList :menu="menu.menu"
                        menu-context="burger"
                        :is-extended-main-menu="true"
                        :brand="brand" />
            </template>
          </template>

          <MenuGroupHeadline v-if="$slots['header-user-navi']"
                             data-menu-group-headline="pur-abo"
                             title="PUR Abo" />

          <slot name="header-user-navi" />

          <MenuGroupHeadline :title="`Folge ${rsConfig.actualBrandName}`" />

          <SocialLinks menu-type="header" />
        </div>
      </div>
    </header>
  </div>
</template>
